import type { VoicemailMessage } from '../lib/voicemails';
import { formatDuration, displayNumber } from '../lib/phone';
import { contacts, useContacts } from '../lib/contacts';
import { when } from './Recents';
import { PhoneIcon } from './Icons';

interface Props {
  messages: VoicemailMessage[] | null;
  counts: { new: number; old: number } | null;
  error: string;
  onListen: () => void;
  onCall: (n: string) => void;
}

export function Voicemails({ messages, counts, error, onListen, onCall }: Props) {
  useContacts();
  return (
    <div className="list" data-testid="voicemails">
      <div className="list-head">
        <h2 className="large-title">Voicemail</h2>
        <button type="button" className="link" onClick={onListen} data-testid="vm-listen">
          Listen
        </button>
      </div>
      {counts && (
        <p className="list-summary">
          {counts.new > 0 ? <strong>{counts.new} new</strong> : 'No new messages'}
          {counts.old > 0 && ` · ${counts.old} saved`}
        </p>
      )}
      {error && <div className="empty">{error}</div>}
      {messages?.length === 0 && <div className="empty">No voicemail</div>}
      {messages && messages.length > 0 && (
        <ul className="group">
          {messages.map((m) => {
            const c = contacts.lookup(m.callerNumber);
            const name = c?.name ?? (m.callerName && m.callerName !== m.callerNumber ? m.callerName : '');
            return (
              <li key={m.id} className={`row ${m.isNew ? 'unread' : ''}`} data-testid="vm-item">
                <span className="row-lead">{m.isNew && <span className="dot-new" aria-label="New" />}</span>
                <div className="row-main">
                  <span className="row-title">{name || displayNumber(m.callerNumber) || 'Unknown'}</span>
                  <span className="row-sub">
                    {[name && displayNumber(m.callerNumber), formatDuration(m.seconds), m.isNew && 'New'].filter(Boolean).join(' · ')}
                  </span>
                  {m.transcription && <p className="row-note">{m.transcription}</p>}
                </div>
                <span className="row-trail">
                  <time>{when(m.receivedAt)}</time>
                  {m.callerNumber && (
                    <button type="button" className="icon-btn" aria-label={`Call ${m.callerNumber}`} onClick={() => onCall(m.callerNumber)}>
                      <PhoneIcon width={18} height={18} />
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
